import { useState, useEffect } from "react";
import { portalApi } from "./PortalAuth";
import { Warehouse, Package, PlusCircle, Boxes, ClipboardList, ExternalLink, Copy, CheckCircle2, Loader2, Trash2, ShoppingCart } from "lucide-react";

const Tab = ({ active, onClick, icon: Icon, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition border ${
      active
        ? "bg-[var(--tt-navy)] text-white border-[var(--tt-navy)]"
        : "bg-white text-[var(--tt-navy)] border-[var(--tt-line)] hover:border-[var(--tt-green)] hover:text-[var(--tt-green)]"
    }`}
  >
    <Icon className="h-4 w-4" /> {children}
  </button>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-[var(--tt-muted)] uppercase tracking-widest mb-1.5">{label}</span>
    {children}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full rounded-xl border border-[var(--tt-line)] bg-white px-4 py-2.5 text-sm outline-none focus:border-[var(--tt-green)] transition"
  />
);

// ═══════════════ ENROLL FLOW ═══════════════
const EnrollCard = ({ onEnrolled }) => {
  const [company, setCompany] = useState("");
  const [shopify, setShopify] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const enroll = async () => {
    setBusy(true); setErr("");
    try {
      const r = await portalApi.shipperEnroll({ company_name: company, shopify_domain: shopify });
      onEnrolled(r.data.shipper);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Enrollment failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="card p-8 max-w-2xl mx-auto" data-testid="shipper-enroll-card">
      <div className="flex items-start gap-4">
        <div className="grid place-items-center h-14 w-14 rounded-2xl bg-[var(--tt-mint-soft)] text-[var(--tt-green)] shrink-0">
          <Warehouse className="h-7 w-7" />
        </div>
        <div className="flex-1">
          <h2 className="font-display font-black text-2xl text-[var(--tt-navy)]">Enroll as a shipper</h2>
          <p className="text-sm text-[var(--tt-muted)] mt-1">
            Send us your inventory. We store it, pick it, pack it, and ship it — you focus on selling.
          </p>
        </div>
      </div>
      <div className="mt-6 space-y-4">
        <Field label="Company / brand name">
          <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Goods" data-testid="shipper-enroll-company" />
        </Field>
        <Field label="Shopify store domain (optional)">
          <Input value={shopify} onChange={(e) => setShopify(e.target.value)} placeholder="mystore.myshopify.com" data-testid="shipper-enroll-shopify" />
        </Field>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <button onClick={enroll} disabled={busy} data-testid="shipper-enroll-submit" className="btn-primary w-full">
          {busy ? <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> : null}
          Enroll now
        </button>
      </div>
    </div>
  );
};

// ═══════════════ OVERVIEW TAB ═══════════════
const OverviewTab = ({ shipper, onGoTo }) => {
  const [asns, setAsns] = useState([]);
  const [inv, setInv] = useState([]);
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    portalApi.shipperListASNs().then((r) => setAsns(r.data.asns || []));
    portalApi.shipperInventory().then((r) => setInv(r.data.inventory || []));
    portalApi.shipperOrders().then((r) => setOrders(r.data.orders || []));
  }, []);
  const pending = asns.filter((a) => a.status !== "received").length;
  const openOrders = orders.filter((o) => o.status !== "shipped" && o.status !== "cancelled").length;
  const totalUnits = inv.reduce((s, i) => s + (i.qty || 0), 0);
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <StatCard label="Pending ASNs" value={pending} icon={ClipboardList} onClick={() => onGoTo("asns")} testid="stat-pending-asns" />
      <StatCard label="Units in stock" value={totalUnits} icon={Boxes} onClick={() => onGoTo("inventory")} testid="stat-units-stock" />
      <StatCard label="Open orders" value={openOrders} icon={ShoppingCart} onClick={() => onGoTo("orders")} testid="stat-open-orders" />
      <div className="md:col-span-3 card p-6">
        <div className="font-semibold text-[var(--tt-navy)]">Welcome, {shipper.company_name}</div>
        <p className="text-sm text-[var(--tt-muted)] mt-1">
          Your fulfillment ID is <span className="font-mono text-xs bg-[var(--tt-mint-soft)] text-[var(--tt-navy)] px-2 py-0.5 rounded">{shipper.id.slice(0, 8)}</span>.
          {shipper.shopify_domain ? " Shopify is connected — new orders will flow in automatically." : " Connect your Shopify store from the Shopify tab to auto-import orders."}
        </p>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="card p-5 text-left hover:border-[var(--tt-green)] hover:-translate-y-0.5 transition">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest text-[var(--tt-muted)] font-semibold">{label}</div>
      <Icon className="h-4 w-4 text-[var(--tt-green)]" />
    </div>
    <div className="mt-2 font-display font-black text-4xl text-[var(--tt-navy)]">{value}</div>
  </button>
);

// ═══════════════ ASN TAB ═══════════════
const ASNTab = () => {
  const [asns, setAsns] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => portalApi.shipperListASNs().then((r) => setAsns(r.data.asns || []));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display font-black text-2xl text-[var(--tt-navy)]">Advance Shipping Notices</h2>
          <p className="text-sm text-[var(--tt-muted)] mt-0.5">Tell us what inventory you're sending, so we're ready to receive it.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="asn-new-btn" className="btn-primary text-sm !py-2.5">
          <PlusCircle className="h-4 w-4 inline mr-1.5" /> New ASN
        </button>
      </div>
      {showForm && <ASNForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {asns.length === 0 && !showForm && (
        <div className="card p-10 text-center text-sm text-[var(--tt-muted)]" data-testid="asn-empty">
          No ASNs yet. Create one to let the warehouse know what's coming.
        </div>
      )}
      <div className="space-y-3">
        {asns.map((a) => (
          <div key={a.id} className="card p-4" data-testid={`asn-row-${a.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-[var(--tt-navy)]">{a.reference}</div>
                <div className="text-xs text-[var(--tt-muted)] mt-0.5">
                  {a.total_units} units · {a.items?.length || 0} SKUs
                  {a.carrier ? ` · via ${a.carrier}` : ""}
                  {a.tracking ? ` · ${a.tracking}` : ""}
                </div>
              </div>
              <StatusPill status={a.status} received={a.received_units} total={a.total_units} />
            </div>
            <div className="mt-3 grid md:grid-cols-3 gap-2">
              {(a.items || []).map((it, i) => (
                <div key={i} className="text-xs bg-[var(--tt-bg)] rounded-lg px-3 py-2">
                  <div className="font-mono text-[var(--tt-navy)]">{it.sku}</div>
                  <div className="text-[var(--tt-muted)]">{it.name || "—"} · {it.qty} units</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatusPill = ({ status, received, total }) => {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    partial: "bg-blue-100 text-blue-800",
    received: "bg-emerald-100 text-emerald-800",
    new: "bg-slate-100 text-slate-700",
    picking: "bg-blue-100 text-blue-800",
    picked: "bg-indigo-100 text-indigo-800",
    packed: "bg-violet-100 text-violet-800",
    shipped: "bg-emerald-100 text-emerald-800",
    backorder: "bg-red-100 text-red-800",
    cancelled: "bg-red-100 text-red-800",
  };
  const cls = map[status] || "bg-slate-100 text-slate-700";
  return (
    <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${cls}`}>
      {status}{typeof received === "number" && typeof total === "number" && status !== "received" ? ` (${received}/${total})` : ""}
    </span>
  );
};

const ASNForm = ({ onClose, onSaved }) => {
  const [ref, setRef] = useState("");
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [items, setItems] = useState([{ sku: "", name: "", qty: 1 }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const setItem = (i, k, v) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addRow = () => setItems([...items, { sku: "", name: "", qty: 1 }]);
  const rmRow = (i) => setItems(items.filter((_, idx) => idx !== i));
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const cleanItems = items.filter((it) => it.sku.trim()).map((it) => ({ sku: it.sku.trim(), name: it.name || undefined, qty: Number(it.qty) || 1 }));
      if (!cleanItems.length) throw new Error("Add at least one SKU");
      await portalApi.shipperCreateASN({ reference: ref || undefined, carrier: carrier || undefined, tracking: tracking || undefined, items: cleanItems });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to create ASN");
    } finally { setBusy(false); }
  };
  return (
    <div className="card p-6 mb-6" data-testid="asn-form">
      <div className="flex items-center justify-between mb-4">
        <div className="font-semibold text-[var(--tt-navy)]">New Advance Shipping Notice</div>
        <button onClick={onClose} className="text-sm text-[var(--tt-muted)] hover:text-[var(--tt-navy)]" data-testid="asn-form-close">Cancel</button>
      </div>
      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <Field label="Reference (optional)"><Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="PO-1024" data-testid="asn-form-ref" /></Field>
        <Field label="Carrier"><Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS, FedEx…" data-testid="asn-form-carrier" /></Field>
        <Field label="Tracking #"><Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="1Z…" data-testid="asn-form-tracking" /></Field>
      </div>
      <div className="text-xs font-semibold text-[var(--tt-muted)] uppercase tracking-widest mb-2">Line items</div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <input value={it.sku} onChange={(e) => setItem(i, "sku", e.target.value)} placeholder="SKU" data-testid={`asn-item-sku-${i}`} className="col-span-3 rounded-lg border border-[var(--tt-line)] px-3 py-2 text-sm outline-none focus:border-[var(--tt-green)] font-mono" />
            <input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="Product name" data-testid={`asn-item-name-${i}`} className="col-span-6 rounded-lg border border-[var(--tt-line)] px-3 py-2 text-sm outline-none focus:border-[var(--tt-green)]" />
            <input value={it.qty} type="number" min="1" onChange={(e) => setItem(i, "qty", e.target.value)} placeholder="Qty" data-testid={`asn-item-qty-${i}`} className="col-span-2 rounded-lg border border-[var(--tt-line)] px-3 py-2 text-sm outline-none focus:border-[var(--tt-green)]" />
            <button onClick={() => rmRow(i)} className="col-span-1 text-[var(--tt-muted)] hover:text-red-600" data-testid={`asn-item-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <button onClick={addRow} className="mt-3 text-xs font-semibold text-[var(--tt-green)] hover:text-[var(--tt-navy)]" data-testid="asn-add-row">+ Add another SKU</button>
      {err && <div className="text-sm text-red-600 mt-3">{err}</div>}
      <div className="mt-5 flex gap-2 justify-end">
        <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-[var(--tt-muted)] hover:text-[var(--tt-navy)]">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="asn-form-save" className="btn-primary text-sm !py-2.5">{busy ? "Saving…" : "Create ASN"}</button>
      </div>
    </div>
  );
};

// ═══════════════ INVENTORY TAB ═══════════════
const InventoryTab = () => {
  const [inv, setInv] = useState([]);
  useEffect(() => { portalApi.shipperInventory().then((r) => setInv(r.data.inventory || [])); }, []);
  return (
    <div>
      <h2 className="font-display font-black text-2xl text-[var(--tt-navy)] mb-1">Inventory on hand</h2>
      <p className="text-sm text-[var(--tt-muted)] mb-5">Live view of what's in our warehouse, updated as we receive and ship.</p>
      {inv.length === 0 ? (
        <div className="card p-10 text-center text-sm text-[var(--tt-muted)]" data-testid="inventory-empty">
          Nothing in stock yet. Submit an ASN and we'll receive it into a bin.
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead className="bg-[var(--tt-bg)] text-xs uppercase tracking-widest text-[var(--tt-muted)]">
              <tr>
                <th className="text-left px-4 py-3">SKU</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Bin</th>
                <th className="text-right px-4 py-3">Qty</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--tt-line)]">
              {inv.map((r, i) => (
                <tr key={i}>
                  <td className="px-4 py-3 font-mono text-[var(--tt-navy)]">{r.sku}</td>
                  <td className="px-4 py-3 text-[var(--tt-ink-soft)]">{r.name || "—"}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-[var(--tt-mint-soft)] text-[var(--tt-navy)] font-mono px-2 py-0.5 rounded">{r.bin_code}</span></td>
                  <td className="px-4 py-3 text-right font-bold text-[var(--tt-navy)]">{r.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ═══════════════ ORDERS TAB ═══════════════
const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  useEffect(() => { portalApi.shipperOrders().then((r) => setOrders(r.data.orders || [])); }, []);
  return (
    <div>
      <h2 className="font-display font-black text-2xl text-[var(--tt-navy)] mb-1">Orders</h2>
      <p className="text-sm text-[var(--tt-muted)] mb-5">Orders imported from your store — we're picking, packing, and shipping them.</p>
      {orders.length === 0 ? (
        <div className="card p-10 text-center text-sm text-[var(--tt-muted)]" data-testid="orders-empty">
          No orders yet. Connect Shopify and place a test order — it will appear here.
        </div>
      ) : (
        <div className="space-y-2" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className="card p-4 flex items-center justify-between" data-testid={`order-${o.id}`}>
              <div>
                <div className="font-semibold text-[var(--tt-navy)]">
                  {o.shopify_order_number ? `#${o.shopify_order_number}` : o.id.slice(0, 8)} · {o.recipient?.name || "—"}
                </div>
                <div className="text-xs text-[var(--tt-muted)] mt-0.5">
                  {o.items?.length || 0} items · {o.recipient?.city || ""} {o.recipient?.state || ""} {o.recipient?.country || ""}
                  {o.tracking_code ? ` · ${o.carrier || "carrier"} ${o.tracking_code}` : ""}
                </div>
              </div>
              <StatusPill status={o.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════ SHOPIFY CONNECT TAB ═══════════════
const ShopifyTab = ({ shipper, onUpdated }) => {
  const [domain, setDomain] = useState(shipper.shopify_domain || "");
  const [secret, setSecret] = useState(shipper.shopify_webhook_secret || "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/shopify/${shipper.id}`;
  const save = async () => {
    setBusy(true); setSaved(false);
    try {
      const r = await portalApi.shipperUpdate({ shopify_domain: domain || null, shopify_webhook_secret: secret || null });
      onUpdated(r.data.shipper);
      setSaved(true);
    } finally { setBusy(false); }
  };
  const copy = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="max-w-3xl">
      <h2 className="font-display font-black text-2xl text-[var(--tt-navy)] mb-1">Shopify connection</h2>
      <p className="text-sm text-[var(--tt-muted)] mb-6">Auto-import orders from your Shopify store — we'll pick, pack, and ship them.</p>

      <div className="card p-6 space-y-4">
        <Field label="Your Shopify store domain">
          <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="mystore.myshopify.com" data-testid="shipper-shopify-domain" />
        </Field>
        <Field label="Webhook secret (from Shopify → Notifications → Webhooks)">
          <Input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Paste the secret Shopify shows when creating the webhook" data-testid="shipper-shopify-secret" />
        </Field>
        <button onClick={save} disabled={busy} data-testid="shipper-shopify-save" className="btn-primary text-sm !py-2.5">
          {busy ? "Saving…" : "Save Shopify settings"}
        </button>
        {saved && <div className="text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Saved.</div>}
      </div>

      <div className="card p-6 mt-4">
        <div className="font-semibold text-[var(--tt-navy)] mb-1">Setup in Shopify</div>
        <ol className="text-sm text-[var(--tt-ink-soft)] space-y-2 mt-3 list-decimal ml-5">
          <li>In your Shopify admin, go to <b>Settings → Notifications → Webhooks</b>.</li>
          <li>Click <b>Create webhook</b>, choose event <b>Order creation</b>, format <b>JSON</b>.</li>
          <li>Paste this URL:</li>
        </ol>
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-[var(--tt-line)] bg-[var(--tt-bg)] p-3">
          <code className="flex-1 text-xs font-mono text-[var(--tt-navy)] break-all">{webhookUrl}</code>
          <button onClick={copy} data-testid="shipper-webhook-copy" className="text-xs font-semibold text-[var(--tt-green)] hover:text-[var(--tt-navy)] inline-flex items-center gap-1">
            {copied ? <><CheckCircle2 className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
          </button>
        </div>
        <ol className="text-sm text-[var(--tt-ink-soft)] space-y-2 mt-3 list-decimal ml-5" start="4">
          <li>Save. Shopify will show a <b>signing secret</b> — copy it and paste into the field above.</li>
          <li>Place a test order — it will appear on your Orders tab within seconds.</li>
        </ol>
      </div>
    </div>
  );
};

// ═══════════════ MAIN PAGE ═══════════════
export const PortalFulfillment = () => {
  const [profile, setProfile] = useState(undefined);
  const [tab, setTab] = useState("overview");
  useEffect(() => { portalApi.shipperProfile().then((r) => setProfile(r.data)); }, []);
  if (profile === undefined) return <div className="text-sm text-[var(--tt-muted)]">Loading…</div>;
  if (!profile.enrolled) return <EnrollCard onEnrolled={(s) => setProfile({ enrolled: true, shipper: s })} />;
  const s = profile.shipper;
  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display font-black text-3xl text-[var(--tt-navy)] tracking-tight">Fulfillment</h1>
        <p className="text-sm text-[var(--tt-muted)] mt-1">Manage ASNs, inventory, and orders across the Turbo Transit warehouse.</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="shipper-tabs">
        <Tab active={tab === "overview"} onClick={() => setTab("overview")} icon={Warehouse} testid="shipper-tab-overview">Overview</Tab>
        <Tab active={tab === "asns"} onClick={() => setTab("asns")} icon={ClipboardList} testid="shipper-tab-asns">ASNs</Tab>
        <Tab active={tab === "inventory"} onClick={() => setTab("inventory")} icon={Boxes} testid="shipper-tab-inventory">Inventory</Tab>
        <Tab active={tab === "orders"} onClick={() => setTab("orders")} icon={ShoppingCart} testid="shipper-tab-orders">Orders</Tab>
        <Tab active={tab === "shopify"} onClick={() => setTab("shopify")} icon={ExternalLink} testid="shipper-tab-shopify">Shopify</Tab>
      </div>
      {tab === "overview" && <OverviewTab shipper={s} onGoTo={setTab} />}
      {tab === "asns" && <ASNTab />}
      {tab === "inventory" && <InventoryTab />}
      {tab === "orders" && <OrdersTab />}
      {tab === "shopify" && <ShopifyTab shipper={s} onUpdated={(sh) => setProfile({ enrolled: true, shipper: sh })} />}
    </div>
  );
};
